import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FaArrowDown,
  FaBuilding,
  FaCalendarAlt,
  FaCheck,
  FaChartLine,
  FaEnvelope,
  FaMapMarkedAlt,
  FaRocket,
  FaShieldAlt,
  FaUsers,
} from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScrollToTop";
import Reveal from "../../../components/ui/Reveal";

import Diaporama from "./Diaporama";
import LastNews from "./LastNews";
import LatestEvents from "./LatestEvents";
import LastGallerie from "./LastGallerie";
import Sections from "./Sections";
import PalmaresTable from "./PalmaresTable";
import PartenairesCarousel from "./PartenairesCarousel";
import FormContact from "./FormContact";
import Footer from "./Footer";
import AppFeatures from "./AppFeatures";
import AppPricing from "./AppPricing";

import { saveVisite } from "../../../services/visitesServices";
import { getSettings } from "../../../services/settingsServices";
import { resolveMediaUrl, unwrapSettings } from "./homeUtils";

import "./Home.css";

const VIEW_VALUES = ["portail", "vitrine"];

function HomePageVitrine() {
  useScrollToTop();

  const [searchParams, setSearchParams] = useSearchParams();

  const initialView = useMemo(() => {
    const q = searchParams.get("view");
    if (VIEW_VALUES.includes(q)) return q;

    const stored = localStorage.getItem("home_view");
    if (VIEW_VALUES.includes(stored)) return stored;

    return "portail";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [view, setView] = useState(initialView);
  const [settings, setSettings] = useState(null);
  const [currentImage, setCurrentImage] = useState("");
  const [fade, setFade] = useState(true);

  const isVitrine = view === "vitrine";

  useEffect(() => {
    localStorage.setItem("home_view", view);

    const sp = new URLSearchParams(searchParams);
    sp.set("view", view);
    setSearchParams(sp, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  useEffect(() => {
    let mounted = true;

    async function fetchSettings() {
      try {
        const response = await getSettings();
        const s = unwrapSettings(response);

        if (!mounted) return;

        setSettings(s);
        setCurrentImage(
          resolveMediaUrl(s?.header1_photo_url, "/img/hero-associations.jpg"),
        );
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres:", error);
      }
    }

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!settings) return;

    const images = [settings.header1_photo_url, settings.header2_photo_url]
      .filter(Boolean)
      .map((img) => resolveMediaUrl(img));

    if (images.length <= 1) return;

    let currentIndex = 0;

    const interval = setInterval(() => {
      setFade(false);

      setTimeout(() => {
        currentIndex = (currentIndex + 1) % images.length;
        setCurrentImage(images[currentIndex]);
        setFade(true);
      }, 450);
    }, 7000);

    return () => clearInterval(interval);
  }, [settings]);

  const getLocalDateKey = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    const today = getLocalDateKey();
    const lastVisitDate = localStorage.getItem("lastVisitDate");

    if (lastVisitDate !== today) {
      saveVisite();
      localStorage.setItem("lastVisitDate", today);
    }
  }, []);

  const scrollToAnchor = (targetId) => {
    const el = document.getElementById(targetId);

    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  const scrollToMain = () => {
    scrollToAnchor(isVitrine ? "presentation" : "sections");
  };

  const handleDemoClick = () => {
    window.location.href = "/inscription";
  };

  const heroTitle = isVitrine
    ? "Un portail moderne pour valoriser toutes les associations de votre commune"
    : settings?.header_title || "Bienvenue sur le portail des associations";

  const heroSubtitle = isVitrine
    ? "MonAppliAsso permet à une mairie, une collectivité ou un omnisports de présenter simplement ses associations : coordonnées, activités, horaires, actualités, événements, photos, lieux de pratique et formulaires de contact."
    : settings?.header_subtitle ||
      "Découvrez les associations, les activités, les événements et toute la vie associative de la commune.";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-white">
      <div className="h-[70px] w-full bg-slate-950" />

      <main>
        <section className="relative min-h-[92vh] overflow-hidden">
          <div
            className={[
              "absolute inset-0 bg-cover bg-center transition-all duration-700",
              fade ? "opacity-100 scale-105" : "opacity-0 scale-100",
            ].join(" ")}
            style={{ backgroundImage: `url('${currentImage}')` }}
          />

          {/* Assombrissement principal de l'image */}
          <div className="absolute inset-0 bg-black/10" />

          {/* Dégradé latéral pour renforcer la lisibilité du texte */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/35" />

          {/* Légère ambiance graphique, beaucoup plus discrète */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_20%,rgba(34,197,94,0.12),transparent_34%),radial-gradient(circle_at_75%_35%,rgba(59,130,246,0.12),transparent_36%)]" />

          <div className="relative z-10 mx-auto flex min-h-[92vh] max-w-7xl flex-col px-4 py-8 sm:px-6 lg:px-8">
            {/* <div className="flex justify-end">
              <div className="rounded-2xl border border-white/15 bg-white/10 p-1 shadow-2xl backdrop-blur-md">
                <button
                  type="button"
                  onClick={() => setView("portail")}
                  className={[
                    "rounded-xl px-4 py-2 text-sm font-bold transition",
                    view === "portail"
                      ? "bg-white text-slate-950 shadow"
                      : "text-white/85 hover:bg-white/10 hover:text-white",
                  ].join(" ")}
                >
                  Voir la Démo
                </button>

                <button
                  type="button"
                  onClick={() => setView("vitrine")}
                  className={[
                    "rounded-xl px-4 py-2 text-sm font-bold transition",
                    view === "vitrine"
                      ? "bg-white text-slate-950 shadow"
                      : "text-white/85 hover:bg-white/10 hover:text-white",
                  ].join(" ")}
                >
                  Offre MonAppliAsso
                </button>
              </div>
            </div> */}

            <div className="grid flex-1 items-center gap-10 py-12 lg:grid-cols-[1.08fr_0.92fr]">
              <Reveal>
                <div className="max-w-4xl">
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 backdrop-blur">
                    {isVitrine ? <FaRocket /> : <FaBuilding />}
                    {isVitrine
                      ? "Solution clé en main pour les communes"
                      : "Vie associative locale"}
                  </span>

                  <h1
                    className="mt-6 text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl xl:text-7xl"
                    dangerouslySetInnerHTML={{ __html: heroTitle }}
                  />

                  <p className="mt-6 max-w-3xl text-base leading-8 text-white/85 sm:text-lg lg:text-xl">
                    {heroSubtitle}
                  </p>

                  <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={scrollToMain}
                      className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-7 py-3.5 text-sm font-bold text-slate-950 shadow-xl transition hover:-translate-y-0.5 hover:shadow-2xl"
                    >
                      {isVitrine
                        ? "Découvrir l’offre"
                        : "Découvrir les associations"}
                      <FaArrowDown />
                    </button>

                    {isVitrine && (
                      <button
                        type="button"
                        onClick={handleDemoClick}
                        className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-7 py-3.5 text-sm font-bold text-white shadow-xl transition hover:-translate-y-0.5 hover:shadow-2xl"
                      >
                        Demander une démonstration
                      </button>
                    )}
                  </div>

                  {isVitrine && (
                    <p className="mt-4 text-sm leading-6 text-white/70">
                      Une solution simple, hébergée et prête à l’emploi pour
                      mieux informer les habitants et valoriser les associations
                      locales.
                    </p>
                  )}
                </div>
              </Reveal>

              <Reveal delay={0.12}>
                {isVitrine ? (
                  <HeroOfferCard onDemoClick={handleDemoClick} />
                ) : (
                  <HeroPortalCard scrollToAnchor={scrollToAnchor} />
                )}
              </Reveal>
            </div>
          </div>
        </section>

        {isVitrine ? (
          <>
            <div id="presentation">
              <AppFeatures />
            </div>

            <Diaporama mode="vitrine" />

            <AppPricing />

            {/* <section className="bg-white py-16 dark:bg-slate-950">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal>
                  <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-950 p-8 text-white shadow-2xl dark:border-white/10 lg:p-12">
                    <div className="grid gap-8 lg:grid-cols-[1fr_0.8fr] lg:items-center">
                      <div>
                        <p className="text-sm font-bold uppercase tracking-[0.25em] text-primary">
                          Pour les communes
                        </p>

                        <h2 className="mt-4 text-3xl font-bold sm:text-4xl">
                          Une vitrine claire pour toutes les associations.
                        </h2>

                        <p className="mt-4 leading-7 text-white/75">
                          La mairie garde la main sur le portail, les
                          informations sont centralisées, et les habitants
                          retrouvent facilement une activité près de chez eux.
                        </p>
                      </div>

                      <div className="grid gap-3">
                        <MiniFeature
                          icon={FaChartLine}
                          text="Suivi des visites et de l’intérêt local"
                        />
                        <MiniFeature
                          icon={FaUsers}
                          text="Gestion des associations et des responsables"
                        />
                        <MiniFeature
                          icon={FaBuilding}
                          text="Infrastructures et lieux de pratique"
                        />
                      </div>
                    </div>
                  </div>
                </Reveal>
              </div>
            </section> */}

            <FormContact mode="vitrine" />
            <Footer view={view} />
          </>
        ) : (
          <>
            <div id="sections">
              <Sections />
            </div>

            <div id="news">
              <LastNews />
            </div>

            <div id="events">
              <LatestEvents />
            </div>

            <div id="palmares">
              <PalmaresTable />
            </div>

            <div id="galerie">
              <LastGallerie />
            </div>

            <div id="partenaires">
              <PartenairesCarousel />
            </div>

            <div id="contact">
              <FormContact />
            </div>

            <Footer view={view} />
          </>
        )}
      </main>
    </div>
  );
}

function HeroOfferCard({ onDemoClick }) {
  const items = [
    "Annuaire complet des associations",
    "Actualités, événements, albums photos et palmarès",
    "Carte, infrastructures et formulaires de contact",
    "Tableau de bord pour la mairie et les responsables",
    "Hébergement inclus dans l’abonnement",
  ];

  return (
    <div className="relative overflow-hidden rounded-[2rem] border border-white/15 bg-black/35 p-6 text-white shadow-2xl backdrop-blur-md sm:p-7">
      <div className="absolute right-0 top-0 h-40 w-40 rounded-bl-full bg-primary/20 blur-2xl" />

      <div className="relative">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/55">
          Offre simple
        </p>

        <h2 className="mt-3 text-2xl font-bold leading-tight sm:text-3xl">
          Un portail associatif complet, sans complexité technique.
        </h2>

        <p className="mt-4 text-sm leading-7 text-white/75">
          Une seule formule pour éviter les options compliquées : tout est
          compris pour présenter, administrer et faire vivre votre portail
          associatif.
        </p>

        <div className="mt-6 rounded-3xl border border-white/15 bg-white p-5 text-slate-950 shadow-xl">
          <p className="text-sm font-bold text-slate-500">
            Formule annuelle tout compris
          </p>

          <div className="mt-2 flex items-end gap-2">
            <span className="text-5xl font-bold">260€</span>
            <span className="pb-2 text-sm font-bold text-slate-500">/ an</span>
          </div>

          <p className="mt-2 text-sm leading-6 text-slate-600">
            Application + hébergement inclus. Une solution claire pour votre
            commune, collectivité ou structure omnisports.
          </p>
        </div>

        <ul className="mt-6 space-y-3">
          {items.map((item) => (
            <li key={item} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] text-white">
                <FaCheck />
              </span>

              <span className="text-sm font-semibold leading-6 text-white/88">
                {item}
              </span>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={onDemoClick}
          className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl"
        >
          Demander une démonstration
        </button>

        <p className="mt-4 text-center text-xs leading-5 text-white/55">
          Programme de parrainage : 10% de réduction sur le prochain abonnement
          pour toute mairie recommandée qui s’abonne.
        </p>
      </div>
    </div>
  );
}

function HeroPortalCard({ scrollToAnchor }) {
  const links = [
    {
      label: "Associations",
      helper: "Trouver une activité",
      icon: FaUsers,
      target: "sections",
    },
    {
      label: "Actualités",
      helper: "Voir les dernières infos",
      icon: FaEnvelope,
      target: "news",
    },
    {
      label: "Événements",
      helper: "Consulter l’agenda",
      icon: FaCalendarAlt,
      target: "events",
    },
    {
      label: "Carte & lieux",
      helper: "Repérer les infrastructures",
      icon: FaMapMarkedAlt,
      target: "sections",
    },
  ];

  return (
    <div className="rounded-[2rem] border border-white/15 bg-black/35 p-6 text-white shadow-2xl backdrop-blur-md sm:p-7">
      {" "}
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-white/55">
        Accès rapide
      </p>
      <h2 className="mt-3 text-2xl font-bold">
        Que souhaitez-vous consulter ?
      </h2>
      <p className="mt-3 text-sm leading-7 text-white/70">
        Retrouvez rapidement les associations, les actualités, les événements et
        les informations utiles de la commune.
      </p>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        {links.map((item) => {
          const Icon = item.icon;

          return (
            <button
              key={item.label}
              type="button"
              onClick={() => scrollToAnchor(item.target)}
              className="group rounded-3xl border border-white/10 bg-white/10 p-4 text-left transition hover:-translate-y-0.5 hover:bg-white/15"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-primary shadow">
                <Icon />
              </span>

              <p className="mt-4 text-base font-bold text-white">
                {item.label}
              </p>

              <p className="mt-1 text-sm text-white/60">{item.helper}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MiniFeature({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/10 p-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-white">
        <Icon />
      </span>

      <span className="font-semibold text-white/90">{text}</span>
    </div>
  );
}

export default HomePageVitrine;
